import { EPerformanceEvents } from '../events';
import { reportPerformanceEvent } from '../report';

/**
 * reader_perf 事件上报字段
 *
 * @description
 * 阅读器**首进**聚合性能埋点（同一 HTML 文档生命周期仅上报一次）。
 * 时间原点为 navigationStart，与 `performance.now()` 对齐（单位：ms）。
 *
 * 核心指标：
 * - reader_page_load_duration：导航起点 → setInitReady（骨架屏消失）
 * - reader_content_render_duration：setInitReady → 首章正文首帧（双 rAF + DOM）
 * - book_detail / chapter_meta / chapter_content / landing：各接口耗时（独立字段）
 */
export interface IReaderPerfTrackParams {
    /** 书籍 ID（drama_id） */
    book_id: string;
    /** 章节 ID；URL 未传时可能为空字符串 */
    chapter_id: string;

    /** 是否由广告落地进入：1 是，0 否（入口 URL 快照，承接剥参后仍保留） */
    is_from_landing: number;
    /**
     * 是否 H5 冷启：1 是，0 否
     * 当前文档内首次 pathname 同步为冷启；同文档 SPA 切页后为温启（见 ../h5-perf-session.ts）
     */
    is_cold_start: number;
    /** 进入阅读器时是否已有 session_ready cookie：1 有，0 无（辅助分析无有效期时的鉴权等待） */
    had_session_cookie: number;
    /** 落地承接流程是否已结束：1 是（含无落地参数直进），0 否（异常中断时可能为 0） */
    landing_flow_done: number;

    /**
     * 子阶段：导航起点 → DOMContentLoaded（ms）；无则不上报
     * 作 fp/fcp 缺失时的 DCL 参考（与 h5_cold_load_perf.dcl_duration 同源）
     */
    static_load_duration: number | null;
    /** 子阶段：ReaderRoot mount → landingIsDone（ms）；无则不上报 */
    landing_wait_duration: number | null;
    /** 子阶段：LandingApi.sendLanding 请求耗时（ms）；仅落地承接路径 */
    landing_api_duration: number | null;
    /** 落地承接接口是否成功：1 成功，0 失败 */
    landing_api_success: number | null;
    /**
     * 子阶段：无 session cookie 时，首章 ReaderApi 发起前累计耗时（ms，相对 navigationStart）
     * 有 cookie 时不采集
     */
    session_wait_duration: number | null;

    /** 书详情接口 /video/video_detail/v1 耗时（ms） */
    book_detail_duration: number | null;
    /** 书详情接口是否成功：1 成功，0 失败 */
    book_detail_success: number | null;
    /** 章节元数据接口 /video/video_model/v1 耗时（ms） */
    chapter_meta_duration: number | null;
    /** 章节元数据接口是否成功：1 成功，0 失败 */
    chapter_meta_success: number | null;
    /** 章节正文 OSS（article_url）请求耗时（ms），与 meta 分开统计 */
    chapter_content_duration: number | null;
    /** 章节正文 OSS 是否成功：1 成功，0 失败 */
    chapter_content_success: number | null;

    /** 主指标：navigationStart → setInitReady（ms） */
    reader_page_load_duration: number;
    /** 主指标：setInitReady → 首章正文首帧（ms）；未到 pageReady 则为 null */
    reader_content_render_duration: number | null;
    /**
     * 参考：navigationStart → 首章正文双 rAF（ms）；fp/fcp 缺失时与 static_load_duration 对照
     * 超时/失败未走正文 rAF 时为 null
     */
    raf_paint_duration: number | null;

    /** 首帧时是否有可读正文：1 有（.reader_text/.reader_title_text），0 无（付费墙/空内容/超时） */
    has_body_content: number;
    /** 正文渲染是否在 10s 内未完成而超时兜底：1 是，0 否 */
    render_timeout: number;
    /** 失败阶段标识，如 chapter_fetch；成功路径为 null */
    fail_stage: string | null;
}

/**
 * 阅读器首进性能聚合埋点（reader_perf）
 *
 * @description
 * 由 reader-perf-timing 在正文首帧、首章失败或渲染超时后调用。
 * 与全局 page_data_loaded 独立，勿混用口径。
 */
export function logReaderPerfTrack(params: IReaderPerfTrackParams): void {
    const {
        book_id,
        chapter_id,
        is_from_landing,
        is_cold_start,
        had_session_cookie,
        landing_flow_done,
        static_load_duration,
        landing_wait_duration,
        landing_api_duration,
        landing_api_success,
        session_wait_duration,
        book_detail_duration,
        book_detail_success,
        chapter_meta_duration,
        chapter_meta_success,
        chapter_content_duration,
        chapter_content_success,
        reader_page_load_duration,
        reader_content_render_duration,
        raf_paint_duration,
        has_body_content,
        render_timeout,
        fail_stage,
    } = params;

    const payload: Record<string, unknown> = {
        book_id,
        chapter_id,
        is_from_landing,
        is_cold_start,
        had_session_cookie,
        landing_flow_done,
        has_body_content,
        render_timeout,
        reader_page_load_duration,
    };

    if (static_load_duration != null) {
        payload.static_load_duration = static_load_duration;
    }
    if (landing_wait_duration != null) {
        payload.landing_wait_duration = landing_wait_duration;
    }
    if (landing_api_duration != null) {
        payload.landing_api_duration = landing_api_duration;
        payload.landing_api_success = landing_api_success;
    }
    if (session_wait_duration != null) {
        payload.session_wait_duration = session_wait_duration;
    }
    if (book_detail_duration != null) {
        payload.book_detail_duration = book_detail_duration;
        payload.book_detail_success = book_detail_success;
    }
    if (chapter_meta_duration != null) {
        payload.chapter_meta_duration = chapter_meta_duration;
        payload.chapter_meta_success = chapter_meta_success;
    }
    if (chapter_content_duration != null) {
        payload.chapter_content_duration = chapter_content_duration;
        payload.chapter_content_success = chapter_content_success;
    }
    if (reader_content_render_duration != null) {
        payload.reader_content_render_duration = reader_content_render_duration;
    }
    if (raf_paint_duration != null) {
        payload.raf_paint_duration = raf_paint_duration;
    }
    if (fail_stage) {
        payload.fail_stage = fail_stage;
    }

    reportPerformanceEvent(EPerformanceEvents.READER_PERF, payload);
}

// ──────────────────────────────────────────────
// 四步独立上报（替代聚合 reader_perf）
// Step 0（reader_mount）在 ReaderRoot mount 立即发送；
// Step 1-3 各自在数据就绪时立即发送，避免页面提前关闭导致全部丢失
// ──────────────────────────────────────────────

// reader_mount 事件上报字段（Step 0）
interface IReaderMountParams {
    /** 书籍 ID（drama_id） */
    book_id: string;
    /** 章节 ID；URL 未传时可能为空字符串 */
    chapter_id: string;
    /** 是否由广告落地进入：1 是，0 否（入口 URL 快照，承接剥参后仍保留） */
    is_from_landing: number;
    /** 是否 H5 冷启：1 是，0 否（见 ../h5-perf-session.ts） */
    is_cold_start: number;
    /** 进入阅读器时是否已有 session_ready cookie：1 有，0 无 */
    had_session_cookie: number;
    /** 落地页点击 → web 导航起点 的耗时 */
    landing_to_navigation_duration: number | null;
    /** web 导航起点 → 最早 JS 可执行时刻 的耗时 */
    navigation_to_js_duration: number | null;
    /** web 导航起点 → reader 组件 mount 的耗时 */
    reader_mount_duration: number;
    /** web 导航起点 → DCL 的耗时 */
    static_load_duration: number | null;
    /** html请求发出 → 首字节到达（TTFB） 的耗时 */
    ttfb_duration: number | null;
    /** 首字节到达 → 响应接收完成 的耗时 */
    response_duration: number | null;
    /** 响应接收完成 → DOM 可交互 的耗时 */
    dom_interactive_duration: number | null;
}

/**
 * Step 0: 阅读器组件挂载（navigationStart → ReaderRoot mount）
 *
 * @description
 * 在 initReaderPerfFromUrl 中 ReaderRoot mount 后立即发送，
 * 确保即使后续 API 失败或页面提前关闭，早期阶段数据也不丢失。
 */
export function logReaderMount(params: IReaderMountParams): void {
    const {
        book_id,
        chapter_id,
        is_from_landing,
        is_cold_start,
        had_session_cookie,
        reader_mount_duration,
        landing_to_navigation_duration,
        navigation_to_js_duration,
        static_load_duration,
        ttfb_duration,
        response_duration,
        dom_interactive_duration,
    } = params;

    const payload: Record<string, unknown> = {
        book_id,
        chapter_id,
        is_from_landing,
        is_cold_start,
        had_session_cookie,
        reader_mount_duration,
    };
    if (landing_to_navigation_duration != null) {
        payload.landing_to_navigation_duration = landing_to_navigation_duration;
    }
    if (navigation_to_js_duration != null) {
        payload.navigation_to_js_duration = navigation_to_js_duration;
    }
    if (static_load_duration != null) {
        payload.static_load_duration = static_load_duration;
    }
    if (ttfb_duration != null) {
        payload.ttfb_duration = ttfb_duration;
    }
    if (response_duration != null) {
        payload.response_duration = response_duration;
    }
    if (dom_interactive_duration != null) {
        payload.dom_interactive_duration = dom_interactive_duration;
    }

    reportPerformanceEvent(EPerformanceEvents.READER_MOUNT, payload);
}

interface IReaderProfileHydrationParams {
    book_id: string;
    chapter_id: string;
    is_from_landing: number;
    is_cold_start: number;
    had_session_cookie: number;
    profile_hydration_wait_duration: number;
}

/**
 * profile hydration 等待耗时（useLandingData 中 await getProfileHydrationInflight）
 *
 * @description
 * 在 markProfileHydrationWaitDuration 中立即发送，
 * 确保即使后续页面加载失败，该阶段数据也不丢失。
 */
export function logReaderProfileHydration(params: IReaderProfileHydrationParams): void {
    const { book_id, chapter_id, is_from_landing, is_cold_start, had_session_cookie, profile_hydration_wait_duration } =
        params;

    reportPerformanceEvent(EPerformanceEvents.READER_PROFILE_HYDRATION, {
        book_id,
        chapter_id,
        is_from_landing,
        is_cold_start,
        had_session_cookie,
        profile_hydration_wait_duration,
    });
}

interface IReaderPageLoadParams {
    book_id: string;
    chapter_id: string;
    is_from_landing: number;
    is_cold_start: number;
    had_session_cookie: number;
    landing_flow_done: number;
    reader_page_load_duration: number;
    static_load_duration: number | null;
    landing_wait_duration: number | null;
    landing_api_duration: number | null;
    landing_api_success: number | null;
    session_wait_duration: number | null;
    /** pagehide 时强制发送标记 */
    page_hidden?: number;
    /** navigationStart → ReaderRoot mount（ms） */
    reader_mount_duration: number | null;
    /** Navigation Timing：TTFB（ms，requestStart → responseStart） */
    ttfb_duration: number | null;
    /** Navigation Timing：响应体下载耗时（ms，responseStart → responseEnd） */
    response_duration: number | null;
    /** Navigation Timing：DOM 可交互耗时（ms，responseEnd → domInteractive） */
    dom_interactive_duration: number | null;
    /** 子阶段：user profile hydration 等待耗时（ms）；仅落地承接路径，无 in-flight 时为 null */
    profile_hydration_wait_duration: number | null;
}

/** Step 1: 阅读器页面加载（navigationStart → 骨架屏消失） */
export function logReaderPageLoad(params: IReaderPageLoadParams): void {
    const {
        book_id,
        chapter_id,
        is_from_landing,
        is_cold_start,
        had_session_cookie,
        landing_flow_done,
        reader_page_load_duration,
        static_load_duration,
        landing_wait_duration,
        landing_api_duration,
        landing_api_success,
        session_wait_duration,
        page_hidden,
        reader_mount_duration,
        ttfb_duration,
        response_duration,
        dom_interactive_duration,
        profile_hydration_wait_duration,
    } = params;

    const payload: Record<string, unknown> = {
        book_id,
        chapter_id,
        is_from_landing,
        is_cold_start,
        had_session_cookie,
        landing_flow_done,
        reader_page_load_duration,
        page_hidden: page_hidden ?? 0,
    };
    if (static_load_duration != null) {
        payload.static_load_duration = static_load_duration;
    }
    if (landing_wait_duration != null) {
        payload.landing_wait_duration = landing_wait_duration;
    }
    if (landing_api_duration != null) {
        payload.landing_api_duration = landing_api_duration;
        payload.landing_api_success = landing_api_success;
    }
    if (session_wait_duration != null) {
        payload.session_wait_duration = session_wait_duration;
    }
    if (reader_mount_duration != null) {
        payload.reader_mount_duration = reader_mount_duration;
    }
    if (ttfb_duration != null) {
        payload.ttfb_duration = ttfb_duration;
    }
    if (response_duration != null) {
        payload.response_duration = response_duration;
    }
    if (dom_interactive_duration != null) {
        payload.dom_interactive_duration = dom_interactive_duration;
    }
    if (profile_hydration_wait_duration != null) {
        payload.profile_hydration_wait_duration = profile_hydration_wait_duration;
    }

    reportPerformanceEvent(EPerformanceEvents.READER_PAGE_LOAD, payload);
}

interface IReaderApiLoadParams {
    book_id: string;
    chapter_id: string;
    is_from_landing: number;
    is_cold_start: number;
    /** 'initial' 首屏（含 book_detail），'chapter_switch' 切换章节（仅 chapter_meta + chapter_content） */
    load_type: 'initial' | 'chapter_switch';
    book_detail_duration: number | null;
    book_detail_success: number | null;
    chapter_meta_duration: number | null;
    chapter_meta_success: number | null;
    chapter_content_duration: number | null;
    chapter_content_success: number | null;
    session_wait_duration: number | null;
    /** navigationStart → book_detail API 发起时刻（ms） */
    book_detail_start?: number | null;
    /** navigationStart → chapter_meta API 发起时刻（ms） */
    chapter_meta_start?: number | null;
    /** navigationStart → chapter_content API 发起时刻（ms） */
    chapter_content_start?: number | null;
}

/** Step 2: 阅读器接口加载耗时 */
export function logReaderApiLoad(params: IReaderApiLoadParams): void {
    const {
        book_id,
        chapter_id,
        is_from_landing,
        is_cold_start,
        load_type,
        book_detail_duration,
        book_detail_success,
        chapter_meta_duration,
        chapter_meta_success,
        chapter_content_duration,
        chapter_content_success,
        session_wait_duration,
        book_detail_start,
        chapter_meta_start,
        chapter_content_start,
    } = params;

    const payload: Record<string, unknown> = {
        book_id,
        chapter_id,
        is_from_landing,
        is_cold_start,
        load_type,
    };
    if (book_detail_duration != null) {
        payload.book_detail_duration = book_detail_duration;
        payload.book_detail_success = book_detail_success;
    }
    if (chapter_meta_duration != null) {
        payload.chapter_meta_duration = chapter_meta_duration;
        payload.chapter_meta_success = chapter_meta_success;
    }
    if (chapter_content_duration != null) {
        payload.chapter_content_duration = chapter_content_duration;
        payload.chapter_content_success = chapter_content_success;
    }
    if (session_wait_duration != null) {
        payload.session_wait_duration = session_wait_duration;
    }
    if (book_detail_start != null) {
        payload.book_detail_start = book_detail_start;
    }
    if (chapter_meta_start != null) {
        payload.chapter_meta_start = chapter_meta_start;
    }
    if (chapter_content_start != null) {
        payload.chapter_content_start = chapter_content_start;
    }

    reportPerformanceEvent(EPerformanceEvents.READER_API_LOAD, payload);
}

interface IReaderContentRenderParams {
    book_id: string;
    chapter_id: string;
    is_from_landing: number;
    is_cold_start: number;
    reader_content_render_duration: number;
    /** 落地页点击 → 阅读器首章正文首帧（ms）；仅有有效点击时间且正文成功展示时上报。 */
    landing_to_content_render_duration?: number | null;
    raf_paint_duration: number | null;
    has_body_content: number;
    render_timeout: number;
    fail_stage: string | null;
    /** pagehide 时强制发送标记 */
    page_hidden?: number;
}

/** Step 3: 阅读器内容渲染完整耗时（navigationStart → 正文首帧） */
export function logReaderContentRender(params: IReaderContentRenderParams): void {
    const {
        book_id,
        chapter_id,
        is_from_landing,
        is_cold_start,
        reader_content_render_duration,
        landing_to_content_render_duration,
        raf_paint_duration,
        has_body_content,
        render_timeout,
        fail_stage,
        page_hidden,
    } = params;

    const payload: Record<string, unknown> = {
        // 本次首进跟踪的书籍 ID 和章节 ID。
        book_id,
        chapter_id,
        // 推广来源：1 是，0 否
        is_from_landing,
        // 入口冷启标记：1 整页冷启，0 同文档 SPA 切页；
        is_cold_start,
        // Web 导航开始 → 本次上报触发时刻（ms）；正常为正文首帧，异常为失败/超时/隐藏时刻。
        reader_content_render_duration,
        // 首帧是否有正文：1 内容非空、非锁定付费墙且检测到正文或标题 DOM；0 无正文或异常兜底。
        has_body_content,
        // 1 渲染等待达到 10s 或页面提前隐藏时兜底；0 未标记超时，不代表正文一定加载成功。
        render_timeout,
        // 1 首进跟踪未结束时页面变为隐藏而补报；0 非隐藏兜底上报。
        page_hidden: page_hidden ?? 0,
    };
    // 复用落地页 click_to_jump 的绝对时间戳，统计跨页面的首章正文展示总耗时。
    if (landing_to_content_render_duration != null) {
        payload.landing_to_content_render_duration = landing_to_content_render_duration;
    }
    // Web 导航开始 → 首章双 rAF 回调的耗时（ms），作为首帧参考；未执行该回调时不上报。
    if (raf_paint_duration != null) {
        payload.raf_paint_duration = raf_paint_duration;
    }
    // 已记录的失败阶段，如 chapter_fetch；未记录时不上传，缺失不代表正文一定加载成功。
    if (fail_stage) {
        payload.fail_stage = fail_stage;
    }

    reportPerformanceEvent(EPerformanceEvents.READER_CONTENT_RENDER, payload);
}
